import debounce from 'lodash/debounce';
import { useEffect, useRef } from 'react';
import type { UseRefinementListProps } from 'react-instantsearch';
import { useRefinementList } from 'react-instantsearch';
import { useClearRefinements } from 'react-instantsearch-core';

import { Configure } from '@/components/Configure';
import type { InputComponentProps } from '@/components/DeprecatedInput';
import { DeprecatedInput } from '@/components/DeprecatedInput';
import { useRouter } from '@/hooks/useRouter';
import { DEBOUNCE_FILTER_TIME_IN_MS } from '@/lib/const';
import { useRefNumberContext } from '@/providers/RefNumberProvider/RefNumberProviderContext';

interface TypeDropdownProps
  extends UseRefinementListProps,
    InputComponentProps {
  placeholder: string;
}

export const RefNumberFilter = (props: TypeDropdownProps) => {
  const { query } = useRouter();
  const skuValue = (query['objectID'] ?? '') as string;
  const { refNumber, setRefNumber } = useRefNumberContext();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { refine: clearRefinement } = useClearRefinements({
    includedAttributes: [props.attribute],
  });
  const { refine } = useRefinementList({
    ...props,
  });

  const debouncedKeyUpHandler = useRef(
    debounce((latestRefNumber: string) => {
      clearRefinement();

      if (latestRefNumber !== '') {
        refine(latestRefNumber);
      }
    }, DEBOUNCE_FILTER_TIME_IN_MS),
  ).current;

  const handleKeyUp = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    debouncedKeyUpHandler(evt.currentTarget.value);
  };

  const handlePaste = (evt: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = evt.clipboardData.getData('Text');
    handleKeyUp({
      currentTarget: { value: pastedValue },
    } as React.KeyboardEvent<HTMLInputElement>);
  };

  useEffect(() => {
    setRefNumber(skuValue.trim().toUpperCase());
  }, []);

  return (
    <>
      <Configure restrictSearchableAttributes={['objectID', 'name']} />
      <DeprecatedInput
        ref={inputRef}
        data-test-id='RefNumberFilterDeprecatedInput'
        onChange={(evt) => {
          setRefNumber(evt.target.value.trim().toUpperCase());
        }}
        onKeyUp={handleKeyUp}
        onPaste={handlePaste}
        value={refNumber ?? ''}
        variant='boxed'
        {...props}
      />
    </>
  );
};

RefNumberFilter.displayName = 'RefNumberFilter';
